import { useManageCaseContext } from '../lib/context/ManageCaseContext';
import { Case } from '../schemas/shared';
import { ActionItem } from '../components/Table/TableDataView';
import { updateCaseSubSpecialty } from 'vl-common/src/components/Cases/updateCaseSubSpecialty';
import { useRuntimeConfig } from './Runtime';
import useIsAuditReferrer from './useIsAuditReferrer';
import useIsClientAdmin from './useIsClientAdmin';
import useIsReferrerAdmin from './useIsReferrerAdmin';
import useIsReferrer from './useIsReferrer';

type UseCaseManagementActionsParams = {
  refetchFn: () => void;
};

const useCaseManagementActions = (params: UseCaseManagementActionsParams) => {
  const { UBRN_ENABLED } = useRuntimeConfig();
  const isAuditReferrer = useIsAuditReferrer();
  const isClientAdmin = useIsClientAdmin();
  const isReferrerAdmin = useIsReferrerAdmin();
  const isReferrer = useIsReferrer();

  const {
    onSetActionedCase,
    openChangeCaseRefModal,
    openAddNotesModal,
    openViewNotesModal,
    openAddUbrnModal,
    openRemoveQuotaModal,
    openAddQuotaModal,
    openDischargeModal,
    openChangePriorityModal,
    onCaseReopen,
    onBookCaseAppointment
  } = useManageCaseContext();

  const withActioned = (activeCase: Case, fn: (activeCase: Case) => void) => () => {
    onSetActionedCase({ activeCase, refetchFn: params.refetchFn });
    fn(activeCase);
  };

  const generateCaseActions = (caseData: Case & { [key: string]: unknown }): ActionItem[] => {
    const isActiveCase = !caseData.case_closed_date;
    const isBookable = caseData.appt_bookable;

    return [
      {
        label: 'Book Appointment',
        isVisible: () => !isReferrer && !isReferrerAdmin,
        isDisabled: () => !isBookable,
        onClick: withActioned(caseData, onBookCaseAppointment)
      },
      {
        label: 'Add Note',
        onClick: withActioned(caseData, openAddNotesModal)
      },
      {
        label: 'View Notes',
        onClick: withActioned(caseData, openViewNotesModal)
      },
      {
        label: 'Add UBRN',
        onClick: withActioned(caseData, openAddUbrnModal),
        isVisible: () => !!Number(UBRN_ENABLED) && !isAuditReferrer
      },
      {
        label: 'Update Case Sub-Specialty',
        onClick: () => updateCaseSubSpecialty(caseData, params.refetchFn),
        isVisible: () => isActiveCase && !!caseData.case_spec_area_code && isClientAdmin
      },
      {
        label: 'Update Case Ref',
        onClick: withActioned(caseData, openChangeCaseRefModal),
        isVisible: () => isActiveCase && !isAuditReferrer
      },
      {
        label: 'Re-open Case',
        onClick: withActioned(caseData, () => onCaseReopen(caseData.case_id)),
        isVisible: () => !isActiveCase
      },
      {
        label: 'Change Priority',
        onClick: withActioned(caseData, openChangePriorityModal),
        // @ts-expect-error
        isVisible: () => caseData?.spec_cat_urg_count > 1 && caseData.case_status === 'NEW'
      },
      {
        label: 'Discharge case',
        onClick: withActioned(caseData, openDischargeModal),
        isVisible: () => isActiveCase && caseData.cancel_flag && !isAuditReferrer
      },

      {
        label: 'Remove Quota',
        onClick: withActioned(caseData, openRemoveQuotaModal),
        isVisible: () => caseData.patient_appt_quota > 0
      },

      {
        label: 'Add Virtual Quota',
        onClick: withActioned(caseData, openAddQuotaModal),
        isVisible: () => caseData.patient_appt_quota === 0
      }
    ];
  };

  return { generateCaseActions };
};

export default useCaseManagementActions;
